import { useAuth } from "@/contexts/AuthContext";
import { fetchNaoLidas, montarIdentity } from "@/services/SuporteChat";
import { AlertTypes, Utils } from "@/utils/Utils";
import { MaterialCommunityIcons, MaterialIcons } from "@/ui/icons";
import * as Application from "@/platform/application";
import { useIsFocused, useRouter } from "@/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator } from "@/ui/ActivityIndicator";
import { AppState } from "@/platform/app-state";
import { DeviceEventEmitter } from "@/platform/events";
import { Image } from "@/ui/Image";
import { Platform } from "@/platform/platform";
import { PullToRefresh } from "@/ui/PullToRefresh";
import { cx } from "@/ui/cx";
import logoTirvuBranco from "@/assets/images/logo_tirvu_horizontal_branco.png";
import EmpresaSwitcher, { EmpresaSwitcherRef } from "./EmpresaSwitcher";
import { useEmpresaLogo } from "./empresaLogo";
import s from "./PageFragment.module.css";

export const PageFragment = ({
  children,
  pageIcon,
  pageTitle,
  pageLabel,
  spaceHeightOffset = 0,
  headerPaddingBottom = 65,
  scrollEnabled = true,
  componentAvoidViewFixed = null,
  componentAvoidViewButtomFixed = null,
  refreshing = false,
  onRefresh: onRefreshProp,
  loading = false,
  SkeletonComponent,
  scrollViewRef,
  empresaBoxRef,
  chatBtnRef,
  notifBtnRef,
  disableFade = false,
}: {
  children: React.ReactNode;
  pageIcon: any;
  pageTitle: string;
  pageLabel: string;
  spaceHeightOffset: number;
  /**
   * Altura da faixa AZUL abaixo do rótulo da página. O padrão (65) existe para o
   * `componentAvoidViewFixed` subir por cima dela com margem negativa — é assim que
   * OrdemServico e Arquivos encaixam suas barras fixas.
   *
   * ⚠️ Azul e vão dividem o MESMO orçamento: o que o componente fixo não cobrir fica
   * azul, e o que ele cobrir vira distância até o primeiro card. Tela sem barra fixa (ou
   * com uma barra baixa) precisa de um orçamento menor, senão sobra vazio — em azul, em
   * cinza, ou nos dois. Só mexa aqui na SUA tela; o default serve o resto do app.
   */
  headerPaddingBottom?: number;
  scrollEnabled?: boolean;
  componentAvoidViewFixed?: any;
  componentAvoidViewButtomFixed?: any;
  refreshing?: boolean;
  onRefresh?: () => void;
  loading?: boolean;
  SkeletonComponent?: React.ReactNode;
  /**
   * Ref opcional do ScrollView da página (usado pelo tour do Home p/ rolar até as seções).
   * WEB: aponta para o <div> rolável (use `scrollTo({ top, behavior })`).
   */
  scrollViewRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * Refs opcionais dos itens do header (usados pelo tour do Home p/ destacá-los).
   * WEB: apontam para os <button> do header (meça com `getBoundingClientRect()`).
   */
  empresaBoxRef?: React.RefObject<HTMLElement | null>;
  chatBtnRef?: React.RefObject<HTMLElement | null>;
  notifBtnRef?: React.RefObject<HTMLElement | null>;
  /** Desliga o fade-in de entrada (entra direto opaco, sem piscar branco). */
  disableFade?: boolean;
}) => {
  const PRIMARY = "#294956";
  // WEB: `onRefresh = () => {}` continua sendo o default; guardamos se a tela passou um
  // de verdade para só então mostrar o botão "Atualizar" do desktop.
  const temOnRefresh = typeof onRefreshProp === "function";
  const onRefresh = onRefreshProp ?? (() => {});
  // WEB: `insets.top` do header virou `var(--safe-top)` no CSS Module.
  // WEB: Animated.Value(disableFade ? 1 : 0) → keyframe CSS de 350ms decidido na montagem
  // (mesma semântica: valor inicial fixado no 1º render; mudar `disableFade` depois não
  // reinicia o fade).
  const fadeNaMontagem = useRef(!disableFade).current;

  // WEB: o Animated.timing(fade → 1, 350ms) virou a animação `.raizFade` do CSS Module
  // (entra direto opaco quando `disableFade` — sem animação/piscada).

  return (
    <div className={cx(s.raiz, fadeNaMontagem && s.raizFade)}>
      <CabecalhoPagina
        pageIcon={pageIcon}
        pageTitle={pageTitle}
        pageLabel={pageLabel}
        headerPaddingBottom={headerPaddingBottom}
        refreshing={refreshing}
        onRefresh={temOnRefresh ? onRefresh : undefined}
        empresaBoxRef={empresaBoxRef}
        chatBtnRef={chatBtnRef}
        notifBtnRef={notifBtnRef}
      />
      {componentAvoidViewFixed ? (
        <div className={s.slotFixo}>{componentAvoidViewFixed}</div>
      ) : null}
      {/* WEB: ScrollView + RefreshControl → <PullToRefresh> (é o contêiner de rolagem).
          `scrollEnabled=false` → overflow travado; indicador de rolagem escondido
          (`sem-scrollbar`), como `showsVerticalScrollIndicator={false}`. */}
      <PullToRefresh
        refreshing={refreshing}
        onRefresh={onRefresh}
        color={PRIMARY}
        scrollRef={scrollViewRef}
        className={cx(s.scroll, "sem-scrollbar")}
        style={scrollEnabled ? undefined : { overflowY: "hidden" }}
      >
        {/* contentContainerStyle: marginTop -30 + paddingHorizontal 18 + paddingBottom
            spaceHeightOffset.
            WEB: `Platform.OS` é "web", então vale o ramo Android (paddingTop 0) — a
            referência validada: os 30px que sobem acima da borda do contêiner ficam
            recortados (overflow negativo no topo não é rolável), igual ao Android. O
            `contentInsetAdjustmentBehavior="never"` do iOS não tem equivalente no browser. */}
        <div
          className={s.conteudo}
          style={
            {
              paddingTop: Platform.OS === "ios" ? 30 : 0,
              "--pf-espaco-inferior": `${spaceHeightOffset}px`,
            } as React.CSSProperties
          }
        >
          {loading ? (
            <div className={s.skeleton}>{SkeletonComponent}</div>
          ) : (
            children
          )}
        </div>
      </PullToRefresh>
      {componentAvoidViewButtomFixed ? (
        <div className={s.slotInferior}>{componentAvoidViewButtomFixed}</div>
      ) : null}
    </div>
  );
};

/**
 * Cabeçalho azul do app (empresa, logo Tirvu, atualizar, chat, notificações e título da
 * página). Usado pelo PageFragment e, no desktop, por telas de pilha que mantêm o shell
 * (ex.: chat de suporte) — o mesmo cabeçalho em todo lugar.
 */
export function CabecalhoPagina({
  pageIcon,
  pageTitle,
  pageLabel,
  headerPaddingBottom = 65,
  refreshing = false,
  onRefresh,
  empresaBoxRef,
  chatBtnRef,
  notifBtnRef,
}: {
  pageIcon?: any;
  pageTitle?: string;
  pageLabel?: string;
  headerPaddingBottom?: number;
  refreshing?: boolean;
  onRefresh?: () => void;
  empresaBoxRef?: React.RefObject<HTMLElement | null>;
  chatBtnRef?: React.RefObject<HTMLElement | null>;
  notifBtnRef?: React.RefObject<HTMLElement | null>;
}) {
  const PRIMARY = "#294956";
  const temOnRefresh = typeof onRefresh === "function";
  const switcherRef = useRef<EmpresaSwitcherRef>(null);

  const { idColaborador, userData } = useAuth();
  const empresaLogo = useEmpresaLogo();
  const router = useRouter();
  const isFocused = useIsFocused();
  // Badge do sino só aparece se houver notificação NÃO lida (lido = 0).
  const temNotificacoesNaoLidas =
    Array.isArray((userData as any)?.notificacoes) &&
    (userData as any).notificacoes.some((n: any) => Number(n?.lido ?? 0) === 0);
  const [naoLidasSuporte, setNaoLidasSuporte] = useState(0);
  // Loader no logo do header enquanto troca de empresa (emitido pelo EmpresaSwitcher).
  const [trocandoEmpresa, setTrocandoEmpresa] = useState(false);

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(
      "empresa-troca-loading",
      (v: boolean) => setTrocandoEmpresa(!!v),
    );
    return () => sub.remove();
  }, []);

  // Rede de segurança: se o evento de conclusão não chegar, desliga o loader sozinho.
  useEffect(() => {
    if (!trocandoEmpresa) return;
    const t = setTimeout(() => setTrocandoEmpresa(false), 20000);
    return () => clearTimeout(t);
  }, [trocandoEmpresa]);

  const atualizarBadgeSuporte = useCallback(() => {
    const identity = montarIdentity(idColaborador, userData);
    if (!identity) {
      setNaoLidasSuporte(0);
      return;
    }
    fetchNaoLidas(identity)
      .then(setNaoLidasSuporte)
      .catch(() => {});
  }, [idColaborador, userData]);

  // Atualiza o badge ao focar a tela e quando chega push de suporte / sai do chat.
  useEffect(() => {
    if (isFocused) atualizarBadgeSuporte();
  }, [isFocused, atualizarBadgeSuporte]);

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(
      "suporte-badge-refresh",
      atualizarBadgeSuporte,
    );
    return () => sub.remove();
  }, [atualizarBadgeSuporte]);

  // App volta do background (ex.: após receber um push de suporte) → revalida o badge.
  // useIsFocused não muda só por foreground/background, então sem isto o contador
  // só atualizaria ao navegar entre telas.
  useEffect(() => {
    const sub = AppState.addEventListener("change", (state: string) => {
      if (state === "active") atualizarBadgeSuporte();
    });
    return () => sub.remove();
  }, [atualizarBadgeSuporte]);

  // WEB: o Animated.timing(fade → 1, 350ms) virou a animação `.raizFade` do CSS Module
  // (entra direto opaco quando `disableFade` — sem animação/piscada).

  const handleLogoPress = () => {
    switcherRef.current?.present();
  };

  return (
    <>
      <div
        className={s.header}
        style={{ paddingBottom: headerPaddingBottom }}
      >
        {/* WEB: coluna centralizada (≥768px) dentro da faixa azul full-width. */}
        <div className={s.headerColuna}>
          {/* Top Nav Bar (Logo, Chevron, Chat, Bell) */}
          <div className={s.navBar}>
            {/* Left: Logo Box + Chevron */}
            <button
              type="button"
              ref={empresaBoxRef as React.Ref<HTMLButtonElement>}
              onClick={handleLogoPress}
              className={s.empresaBotao}
              aria-label="Trocar de empresa"
            >
              <div className={s.empresaLogoBox}>
                <Image
                  source={empresaLogo}
                  className={s.empresaLogo}
                  resizeMode="contain"
                  alt=""
                />
                {trocandoEmpresa ? (
                  <div className={s.empresaTrocando}>
                    <ActivityIndicator size="small" color={PRIMARY} />
                  </div>
                ) : null}
              </div>
              <MaterialIcons
                name="keyboard-arrow-down"
                size={20}
                color="#fff"
                style={{ marginLeft: 4 }}
              />
            </button>

            {/* Center: TIRVU Logo + versão do build (abaixo, alinhada à direita do logo).
                Absoluta p/ NÃO alterar o alinhamento vertical da nav bar. Toque → versão + build. */}
            <div className={s.centro}>
              <img
                src={logoTirvuBranco}
                className={s.logoTirvu}
                alt="Tirvu+"
                draggable={false}
              />
              {/* WEB (04/11/2026): sem a versão embaixo da logo — no navegador não há versão de loja. */}
            </div>

            {/* Right: Icons */}
            <div className={s.direita}>
              {/* WEB: no desktop (≥1024px com mouse) não há gesto de puxar para atualizar;
                  um botão discreto chama o mesmo onRefresh (só aparece se a tela passou um). */}
              {temOnRefresh ? (
                <button
                  type="button"
                  onClick={() => onRefresh?.()}
                  disabled={refreshing}
                  className={cx(s.iconeBotao, s.atualizarBotao)}
                  aria-label="Atualizar"
                  title="Atualizar"
                >
                  <div className={s.iconeBox}>
                    {refreshing ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <MaterialIcons name="refresh" size={22} color="#fff" />
                    )}
                  </div>
                </button>
              ) : null}
              <button
                type="button"
                ref={chatBtnRef as React.Ref<HTMLButtonElement>}
                onClick={() => DeviceEventEmitter.emit("open-suporte-sheet")}
                className={s.iconeBotao}
                aria-label="Suporte"
              >
                <div className={s.iconeBox}>
                  <MaterialCommunityIcons
                    name="chat-outline"
                    size={24}
                    color="#fff"
                  />
                  {naoLidasSuporte > 0 ? (
                    <div className={s.badgeSuporte}>
                      <span className={s.badgeSuporteTexto}>
                        {naoLidasSuporte > 99 ? "99+" : naoLidasSuporte}
                      </span>
                    </div>
                  ) : null}
                </div>
              </button>
              <button
                type="button"
                ref={notifBtnRef as React.Ref<HTMLButtonElement>}
                onClick={() => router.push("/(tabs)/notificacoes")}
                className={s.iconeBotao}
                aria-label="Notificações"
              >
                <div className={s.iconeBox}>
                  <MaterialCommunityIcons
                    name="bell-outline"
                    size={24}
                    color="#fff"
                  />
                  {temNotificacoesNaoLidas ? (
                    <div className={s.pontoNotificacao} />
                  ) : null}
                </div>
              </button>
            </div>
          </div>

          {/* Page Info (Title and Label) - restored "as before" */}
          {(pageTitle || pageLabel) && (
            <div className={s.pageInfo}>
              <div className={s.pageTituloLinha}>
                {pageIcon ? (
                  <MaterialIcons name={pageIcon} size={20} color="#fff" />
                ) : null}
                <h1
                  className={s.pageTitulo}
                  style={{ marginLeft: pageIcon ? 8 : 0 }}
                >
                  {pageTitle}
                </h1>
              </div>
              <span className={s.pageLabel}>{pageLabel}</span>
            </div>
          )}
        </div>
      </div>
      <EmpresaSwitcher ref={switcherRef} />
    </>
  );
}
